import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from "react-router-dom";
import SignIn from "@pages/SignIn/SignIn";
import Intro from "@pages/Intro";
import HomeRoutes from "@pages/HomeRoutes/HomeRoutes";
import initI18n from "./i18n";
import { ThemeProvider } from "@mui/material";
import initAmplify from "./amplify";
import AuthGuard from "@components/AuthGuard/AuthGuard";
import SignUp from "@pages/SignUp/SignUp";
import VerifyEmail from "@pages/VerifyEmail/VerifyEmail";
import EmailVerificationGuard from "@components/EmailVerificationGuard/EmailVerificationGuard";
import Auth from "@components/Auth/Auth";
import ResetPassword from "@pages/ResetPassword/ResetPassword";
import Account from "@pages/Account/Account";
import AccountGuard from "@components/AccountGuard/AccountGuard";
import PatientInfo from "@pages/PatientInfo/PatientInfo";
import FamilyInfo from "@pages/FamilyInfo/FamilyInfo";
import Pictures from "@pages/Pictures/Pictures";
import Characters from "@pages/Characters/Characters";
import MessageSnackbar from "@components/MessageSnackbar/MessageSnackbar";
import OnboardingSuccess from "@pages/OnboardingSuccess/OnboardingSuccess";
import { useGetAppConfigQuery } from "@store/appConfigApi";
import Talk from "@pages/Talk/Talk";
import Invite from "@pages/Invite";
import SuccessPageContent from "@components/SuccessPageContent";
import initBrevo from "./brevo";
import getTheme from "./theme";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers";
import OnboardingPageContainer from "@components/Onboarding/OnboardingPageContainer";
import OnboardingBilling from "@components/Onboarding/OnboardingBilling/OnboardingBilling";
import OnboardingCompanion from "@pages/OnboardingCompanion";
import OnboardingLayout from "@components/Onboarding/OnboardingLayout";
import initBetterStack from "./betterstack";
import "driver.js/dist/driver.css";
import IncomingCall from "@components/IncomingCall";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useInitSubscription } from "@hooks/useInitSubscription";
import OfflineBanner from "@components/OfflineBanner";
import { ScrollRestoration } from "react-router-dom";
import Dashboard from "@components/Home/Dashboard";
import ManagePatientsPage from "@pages/ManagePatients/ManagePatientsPage";
import EditPatientInfo from "@components/Home/EditPatientInfo";
import Settings from "@pages/Settings";
import Companions from "@components/Home/Companions";
import Gallery from "@components/Home/Gallery";
import Sessions from "@components/Home/Sessions";
import Schedule from "@components/Home/Schedule";
import PatientDashboard from "@components/Home/PatientDashboard";
import PatientSchedule from "@components/Home/PatientSchedule";
import PatientGallery from "@components/Home/PatientGallery";
import Games from "@components/Home/Games";
import SequenceRecall from "@components/Home/Games/SequenceRecall";
import NBack from "@components/Home/Games/NBack";
import WordFluencyTap from "@components/Home/Games/WordFluencyTap";
import MathBlitz from "@components/Home/Games/MathBlitz";
import PathMemory from "@components/Home/Games/PathMemory";
import ReactionTap from "@components/Home/Games/ReactionTap";
import CategoryTap from "@components/Home/Games/CategoryTap";
import PatternMatch from "@components/Home/Games/PatternMatch";
import RuleSwitch from "@components/Home/Games/RuleSwitch";
import TargetFocus from "@components/Home/Games/TargetFocus";
import { GuidedPreScreen, GuidedPlayFlow } from "@components/Home/Games/GuidedSession";
import PortalLayout from "@components/PortalLayout/PortalLayout";
import { LocaleUtil } from '@utils/locale.util';
import { MobileService } from '@service/mobile.service';

initBetterStack();
initI18n();
initAmplify();
initBrevo(i18n.language);

i18n.on("languageChanged", (lng) => {
  initBrevo(lng);
});

let appReadyNotified = false;
const PENDING_NAV_MAX_AGE_MS = 30_000;
const PENDING_NAV_MAX_ATTEMPTS = 8;

type PendingNavigation = {
  path: string;
  state?: unknown;
  createdAt?: number;
  attempts?: number;
};

const Main = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

function NavigationBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const userLoggedIn = useSelector(
    (state: RootState) => state.auth.user !== null,
  );
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);

  const readPendingNav = (): PendingNavigation | null => {
    const raw = sessionStorage.getItem(MobileService.PENDING_NAV_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as PendingNavigation;
    } catch {
      sessionStorage.removeItem(MobileService.PENDING_NAV_STORAGE_KEY);
      return null;
    }
  };

  useEffect(() => {
    const handler = (e: any) => {
      const { path, state } = e.detail;
      navigate(path, { state, replace: true });
    };

    window.addEventListener('argoli:navigate', handler);
    return () => window.removeEventListener('argoli:navigate', handler);
  }, [navigate]);

  // Cold start / CallKit: acceptReminderCall may run before auth is in Redux; AuthGuard would
  // replace /portal/talk with /sign-in. Re-apply once session is ready using sessionStorage.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const pendingNav = readPendingNav();
    if (!pendingNav || !userLoggedIn) {
      return;
    }
    const attempts = pendingNav.attempts ?? 0;
    const createdAt = pendingNav.createdAt ?? Date.now();
    const isExpired = Date.now() - createdAt > PENDING_NAV_MAX_AGE_MS;
    if (attempts >= PENDING_NAV_MAX_ATTEMPTS || isExpired) {
      sessionStorage.removeItem(MobileService.PENDING_NAV_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(
      MobileService.PENDING_NAV_STORAGE_KEY,
      JSON.stringify({
        ...pendingNav,
        createdAt,
        attempts: attempts + 1,
      }),
    );
    navigate(pendingNav.path, { state: pendingNav.state, replace: true });
  }, [navigate, userLoggedIn, isLoading]);

  useEffect(() => {
    const pendingNav = readPendingNav();
    if (!pendingNav) {
      return;
    }
    if (location.pathname === pendingNav.path) {
      sessionStorage.removeItem(MobileService.PENDING_NAV_STORAGE_KEY);
    }
  }, [location.pathname]);

  return null;
}

const PrivateRoute: FC<PropsWithChildren<any>> = ({ children }) => (
  <AuthGuard>
    <AccountGuard>
      <EmailVerificationGuard>{children}</EmailVerificationGuard>
    </AccountGuard>
  </AuthGuard>
);

const OnboardingRoutes: FC = () => (
  <AuthGuard>
    <EmailVerificationGuard>
      <OnboardingLayout>
        <Outlet />
      </OnboardingLayout>
    </EmailVerificationGuard>
  </AuthGuard>
);

const Layout: FC = () => {
  const isLoggedIn = useSelector(
    (state: RootState) => state.auth.user !== null,
  );

  useEffect(() => {
    if (appReadyNotified) {
      return;
    }
    appReadyNotified = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        MobileService.instance.onAppReady();
      });
    });
  }, []);

  return (
    <Auth>
      {isLoggedIn && (
        <>
          <OfflineBanner />
          <IncomingCall />
        </>
      )}

      <ScrollRestoration />
      <NavigationBridge />
      <Outlet />
    </Auth>
  );
};

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      {
        path: "intro",
        element: <AuthGuard authenticated={false} children={<Intro />} />,
      },
      {
        path: "sign-in",
        element: <AuthGuard authenticated={false} children={<SignIn />} />,
      },
      {
        path: "sign-up",
        element: <AuthGuard authenticated={false} children={<SignUp />} />,
      },
      {
        path: "reset-password",
        element: (
          <AuthGuard authenticated={false} children={<ResetPassword />} />
        ),
      },
      {
        path: "verify-email",
        element: (
          <EmailVerificationGuard verified={false} children={<VerifyEmail />} />
        ),
      },
      {
        path: "/onboarding/*",
        element: <OnboardingRoutes />,
        children: [
          {
            path: "success",
            element: <OnboardingSuccess />,
          },
          {
            path: "billing",
            element: <OnboardingBilling />,
          },
          {
            path: "*",
            element: <OnboardingPageContainer />,
            children: [
              {
                index: true,
                path: "",
                element: <Account />,
              },
              {
                path: "*",
                element: (
                  <AccountGuard>
                    <Outlet />
                  </AccountGuard>
                ),
                children: [
                  {
                    index: true,
                    path: "profile",
                    element: <PatientInfo />,
                  },
                  {
                    path: ":id/profile",
                    element: <PatientInfo />,
                  },
                  {
                    path: "family-info/*",
                    element: <FamilyInfo />,
                  },
                  {
                    path: ":id/family-info/*",
                    element: <FamilyInfo />,
                  },
                  {
                    path: "pictures",
                    element: <Pictures />,
                  },
                  {
                    path: ":id/pictures",
                    element: <Pictures />,
                  },
                  {
                    path: "characters",
                    element: <Characters />,
                  },
                  {
                    path: ":id/characters",
                    element: <Characters />,
                  },
                  {
                    path: "companion/patient",
                    element: <OnboardingCompanion isPatient={true} />,
                  },
                  {
                    path: ":id/companion/patient",
                    element: <OnboardingCompanion isPatient={true} />,
                  },
                  {
                    path: "companion/caregiver",
                    element: <OnboardingCompanion isPatient={false} />,
                  },
                  {
                    path: ":id/companion/caregiver",
                    element: <OnboardingCompanion isPatient={false} />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "talk",
        element: <PrivateRoute children={<Talk isPatient={false} />} />,
      },
      {
        path: "portal/talk",
        element: <PrivateRoute children={<Talk isPatient={true} />} />,
      },
      {
        path: "invite",
        element: <Invite />,
      },
      {
        path: "invite-success",
        element: <SuccessPageContent />,
      },
      {
        path: "*",
        element: <PrivateRoute children={<HomeRoutes />} />,
        children: [
          {
            path: "",
            element: <Dashboard />,
          },
          {
            path: "patients/manage/:id?",
            element: <ManagePatientsPage />,
          },
          {
            index: true,
            path: "patient-info/*",
            element: <EditPatientInfo />,
          },
          {
            path: "settings/*",
            element: <Settings />,
          },
          {
            path: "companions/*",
            element: <Companions />,
          },
          {
            path: "gallery/*",
            element: <Gallery />,
          },
          {
            path: "transcripts/:id?",
            element: <Sessions />,
          },
          {
            path: "schedule/:id?",
            element: <Schedule />,
          },
          {
            path: "games/guided/play",
            element: <GuidedPlayFlow />,
          },
          {
            path: "games/guided",
            element: <GuidedPreScreen />,
          },
          {
            path: "games/sequence-recall",
            element: <SequenceRecall />,
          },
          {
            path: "games/n-back",
            element: <NBack />,
          },
          {
            path: "games/word-fluency-tap",
            element: <WordFluencyTap />,
          },
          {
            path: "games/math-blitz",
            element: <MathBlitz />,
          },
          {
            path: "games/path-memory",
            element: <PathMemory />,
          },
          {
            path: "games/reaction-tap",
            element: <ReactionTap />,
          },
          {
            path: "games/category-tap",
            element: <CategoryTap />,
          },
          {
            path: "games/pattern-match",
            element: <PatternMatch />,
          },
          {
            path: "games/rule-switch",
            element: <RuleSwitch />,
          },
          {
            path: "games/target-focus",
            element: <TargetFocus />,
          },
          {
            path: "games",
            element: <Games />,
          },
          {
            path: "portal",
            element: <PortalLayout />,
            children: [
              {
                path: "",
                element: <PatientDashboard />,
              },
              {
                path: "schedule",
                element: <PatientSchedule />,
              },
              {
                path: "gallery/*",
                element: <PatientGallery />,
              },
              {
                path: "games/guided/play",
                element: <GuidedPlayFlow basePath="portal" />,
              },
              {
                path: "games/guided",
                element: <GuidedPreScreen basePath="portal" />,
              },
              {
                path: "games/sequence-recall",
                element: <SequenceRecall />,
              },
              {
                path: "games/n-back",
                element: <NBack />,
              },
              {
                path: "games/word-fluency-tap",
                element: <WordFluencyTap />,
              },
              {
                path: "games/math-blitz",
                element: <MathBlitz />,
              },
              {
                path: "games/path-memory",
                element: <PathMemory />,
              },
              {
                path: "games/reaction-tap",
                element: <ReactionTap />,
              },
              {
                path: "games/category-tap",
                element: <CategoryTap />,
              },
              {
                path: "games/pattern-match",
                element: <PatternMatch />,
              },
              {
                path: "games/rule-switch",
                element: <RuleSwitch />,
              },
              {
                path: "games/target-focus",
                element: <TargetFocus />,
              },
              {
                path: "games",
                element: <Games />,
              },
              {
                path: "*",
                element: <PatientDashboard />,
              },
            ],
          },
          {
            path: "*",
            element: <Dashboard />,
          },
        ],
      },
    ],
  },
]);

function App() {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<string>(i18n.language);
  useGetAppConfigQuery();
  useInitSubscription();


  useEffect(() => {
    const onLanguageChange = (lng: string) => {
      setLanguage(lng);
    }
    i18n.on('languageChanged', onLanguageChange);
    return () => {
      i18n.off('languageChanged', onLanguageChange);
    };
  }, [i18n]);

  const theme = useMemo(() => {
    return getTheme(language);
  }, [language]);

  const dateAdapterLocale = useMemo(() => {
    const l = LocaleUtil.getDateFnsLocale(language);
    console.log(`Using locale: ${l?.code}`);
    return l;
  }, [language]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={dateAdapterLocale}
    >
      <ThemeProvider theme={theme}>
        <Main>
          <MessageSnackbar />
          <RouterProvider router={router} />
        </Main>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
